// 4. Create the CreatePositionModal component
// components/Position/CreatePositionModal.jsx

import React, { useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import doctoralFields from '../../data/doctoral_fields.json';
import institutionOptions from '../../data/institution_options.json';
import styles from './CreatePosition.module.css';

const fieldOptions = doctoralFields.map(({ code, field }) => ({
  value: field,
  label: `${field} (${code})`,
}));

function CreatePositionModal({ userData, onPositionCreated, onClose }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fields: [],
    city: '',
    country: '',
    link: '',
    institution: '',
    start_semester: '',
    start_year: '',
  });
  const [customFields, setCustomFields] = useState(['', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        fields: formData.fields.includes('other')
          ? customFields.filter(field => field && field.trim() !== '')
          : formData.fields.filter(field => field && field.trim() !== ''),
        native: false
      };

      // Add optional fields only if they have values
      if (formData.institution && formData.institution.trim()) {
        payload.institution = formData.institution.trim();
      }
      if (formData.city && formData.city.trim()) {
        payload.city = formData.city.trim();
      }
      if (formData.country && formData.country.trim()) {
        payload.country = formData.country.trim();
      }
      if (formData.link && formData.link.trim()) {
        let link = formData.link.trim();
        if (!link.startsWith('http://') && !link.startsWith('https://')) {
          link = `https://${link}`;
        }
        payload.link = link;
      }
      if (formData.start_semester && formData.start_semester.trim()) {
        payload.start_semester = formData.start_semester.trim();
      }
      if (formData.start_year && formData.start_year.trim()) {
        payload.start_year = parseInt(formData.start_year);
      }

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/positions`, payload, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          'Content-Type': 'application/json'
        },
      });

      alert('✅ Position created successfully');
      onPositionCreated();
    } catch (error) {
      console.error('Error creating position:', error);
      let errorMessage = '❌ Failed to create position';
      if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(previousData => ({
      ...previousData,
      [field]: value
    }));
  };

  const handleCustomFieldChange = (index, value) => {
    const updatedCustomFields = [...customFields];
    updatedCustomFields[index] = value;
    setCustomFields(updatedCustomFields);
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#F4F3EE",
      borderColor: "#3C6E71",
      padding: "4px"
    })
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Create New Position</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit} className={styles.positionForm}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Position Title *</label>
              <input
                required
                placeholder="e.g., PhD Position in Machine Learning"
                className={styles.formInput}
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Position Description *</label>
              <textarea
                required
                placeholder="Describe the position requirements, responsibilities, and qualifications..."
                rows={4}
                className={styles.formTextarea}
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Institution</label>
              <Select
                options={institutionOptions}
                isClearable
                placeholder="Select Institution"
                className={styles.formSelect}
                value={institutionOptions.find(option => option.value === formData.institution) || null}
                onChange={(option) => handleFieldChange('institution', option?.value || '')}
                filterOption={(option, inputValue) => {
                  // Always show "Other" option when user is typing
                  if (option.value === 'Other') {
                    return inputValue.length > 0;
                  }
                  // Normal filtering for other options
                  return option.label.toLowerCase().includes(inputValue.toLowerCase());
                }}
                noOptionsMessage={({ inputValue }) => {
                  // Show "Other" option when no matches found and user has typed something
                  if (inputValue && inputValue.length > 0) {
                    return 'Type to search or select "Other"';
                  }
                  return 'No options found';
                }}
                styles={customSelectStyles}
                isDisabled={isSubmitting}
                isSearchable
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Research Fields *</label>
              <Select
                isMulti
                options={[...fieldOptions, { value: 'other', label: 'Other' }]}
                placeholder="Select up to 2 fields of study"
                className={styles.formSelect}
                value={fieldOptions.filter(option => formData.fields.includes(option.value)).concat(
                  formData.fields.includes('other') ? [{ value: 'other', label: 'Other' }] : []
                )}
                onChange={(options) => {
                  const values = options ? options.map(option => option.value).slice(0, 2) : [];
                  handleFieldChange('fields', values);
                }}
                filterOption={(option, inputValue) => {
                  // Always show "Other" option when user is typing
                  if (option.value === 'other') {
                    return inputValue.length > 0;
                  }
                  // Normal filtering for other options
                  return option.label.toLowerCase().includes(inputValue.toLowerCase());
                }}
                noOptionsMessage={({ inputValue }) => {
                  // Show "Other" option when no matches found and user has typed something
                  if (inputValue && inputValue.length > 0) {
                    return 'Type to search or select "Other"';
                  }
                  return 'No options found';
                }}
                styles={customSelectStyles}
                isDisabled={isSubmitting}
                isSearchable
              />
            </div>

            {formData.fields.includes('other') && (
              <div className={styles.customFieldsSection}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Custom Field 1</label>
                  <input
                    placeholder="Enter custom research field"
                    className={styles.formInput}
                    value={customFields[0]}
                    onChange={(e) => handleCustomFieldChange(0, e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Custom Field 2</label>
                  <input
                    placeholder="Enter custom research field"
                    className={styles.formInput}
                    value={customFields[1]}
                    onChange={(e) => handleCustomFieldChange(1, e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}

            <div className={styles.locationGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>City</label>
                <input
                  placeholder="e.g., Boston"
                  className={styles.formInput}
                  value={formData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Country</label>
                <input
                  placeholder="e.g., United States"
                  className={styles.formInput}
                  value={formData.country}
                  onChange={(e) => handleFieldChange('country', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start Semester</label>
              <select
                className={styles.formInput}
                value={formData.start_semester}
                onChange={(e) => handleFieldChange('start_semester', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select semester</option>
                <option value="Fall">Fall</option>
                <option value="Spring">Spring</option>
                <option value="Summer">Summer</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start Year</label>
              <select
                className={styles.formInput}
                value={formData.start_year}
                onChange={(e) => handleFieldChange('start_year', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select year</option>
                {Array.from({ length: 6 }, (_, i) => {
                  const year = new Date().getFullYear() + i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Additional Information Link (Optional)</label>
              <input
                placeholder="https://university.edu/lab-info"
                className={styles.formInput}
                value={formData.link}
                onChange={(e) => handleFieldChange('link', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Position'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreatePositionModal;